import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getImg } from '../utils/getImg';
import defaultArt from '../assets/images/dj_kip_art.png';
import {
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Avatar,
  Link,
  Box,
  Grid,
  IconButton,
  Collapse,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useApi } from '../context/ApiProvider';
import {
  ArrowUpward,
  ArrowDownward,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PersonAddAlt1,
  PersonRemoveAlt1,
} from '@mui/icons-material';
import Loading from './Loading';
import SetMobileCard from './SetMobileCard';
import TrackMobileCard from './TrackMobileCard';
import ViewMoreCTA from './ViewMoreCTA';
import { useResponsiveLimits } from '../hooks/useResponsiveLimits';
import LabelCard from './LabelCard';
import HorizontalSection from './sections/HorizontalSection';

const LabelItem = ({ item, asCard }) => {
  const [open, setOpen] = useState(false);
  const [loadingSets, setLoadingSets] = useState(false);
  const [sets, setSets] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [isFollowed, setIsFollowed] = useState(false);
  const [tracks, setTracks] = useState([]);
  const api = useApi();
  let navigate = useNavigate();

  // Screen size detection for responsive limits
  const { getMaxItems } = useResponsiveLimits();

  const {
    art_url_sm,
    name,
    id,
    set_count,
    track_count,
    user_label_stats,
    latest_set_date,
    latest_track_date,
  } = item;

  useEffect(() => {
    const is_followed = user_label_stats?.is_followed || false;
    setIsFollowed(is_followed);
  }, []);

  const labelImgSm = getImg(art_url_sm) || defaultArt;
  useEffect(() => {
    if (set_count > 0 && sets.length === 0 && open) {
      loadLabelSets();
    } else if (track_count > 0 && tracks.length === 0 && open) {
      loadLabelTracks();
    }
  }, [open]);

  async function loadLabelSets() {
    setLoadingSets(true);
    const response = await api.get(`/labels/${id}/sets`, {
      order_by: 'set_meta.release_date',
      order_direction: 'desc',
      search: '',
    });
    if (response.ok) {
      setSets(response.body.data.slice(0, getMaxItems()));
    } else {
      console.log(response);
    }
    setLoadingSets(false);
  }

  async function loadLabelTracks() {
    setLoadingTracks(true);
    const response = await api.get(`/labels/${id}/tracks`, {
      order_by: 'track_meta.release_date',
      order_direction: 'desc',
      search: '',
    });
    if (response.ok) {
      setTracks(response.body.data.slice(0, getMaxItems()));
    } else {
      console.log(response);
    }
    setLoadingTracks(false);
  }

  function goToLabel() {
    navigate(`/labels/${id}`);
  }

  async function handleFollowClick() {
    const response = await api.put(`/stats/label/${id}/follow`);
    if (response.ok) {
      setIsFollowed(response.body.is_followed);
    }
  }

  return (
    <>
      {asCard ? (
        <LabelCard item={item} showDetailedInfo={true} />
      ) : (
        <>
          <TableRow
            sx={{
              '& > *': { borderBottom: 'unset !important' },
              '.MuiTableCell-root': { padding: 1 },
              ':hover': {
                backgroundColor: 'info.main',
                '& .MuiAvatar-root': {
                  borderStyle: 'solid',
                  borderColor: 'primary.main',
                },
              },
            }}
          >
            <TableCell component="th" scope="row">
              <Avatar src={labelImgSm} sx={{ width: 56, height: 56 }} />
            </TableCell>
            <TableCell align="left">
              <Link href={''} to={`/labels/${id}`} underline="hover">
                {name}
              </Link>
            </TableCell>
            <TableCell align="left">{set_count}</TableCell>
            <TableCell align="left">
              {latest_set_date
                ? new Date(latest_set_date).toLocaleDateString()
                : '-'}
            </TableCell>
            <TableCell align="left">{track_count}</TableCell>
            <TableCell align="left">
              {latest_track_date
                ? new Date(latest_track_date).toLocaleDateString()
                : '-'}
            </TableCell>
            <TableCell
              align="right"
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <Tooltip title={isFollowed ? 'Unfollow' : 'Follow'} arrow>
                <IconButton
                  aria-label="expand row"
                  onClick={handleFollowClick}
                  color="primary"
                >
                  {isFollowed ? <PersonRemoveAlt1 /> : <PersonAddAlt1 />}
                </IconButton>
              </Tooltip>
              {(set_count > 0 || track_count > 0) && (
                <IconButton
                  aria-label="expand row"
                  onClick={() => setOpen(!open)}
                  color="primary"
                >
                  {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              )}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                {loadingSets ? (
                  <Box textAlign="center">
                    <Loading alterColor />
                  </Box>
                ) : loadingTracks ? (
                  <Box textAlign="center">
                    <Loading alterColor />
                  </Box>
                ) : sets.length > 0 ? (
                  <Box px={2} pb={2}>
                    <Typography variant="h6" marginTop={3}>
                      Sets
                    </Typography>
                    <Box my={3}>
                      <HorizontalSection
                        items={sets}
                        cardComponent={SetMobileCard}
                        title={null}
                        viewMoreUrl={`/labels/${id}`}
                        cardStyles={{
                          flexShrink: 0,
                          width: '200px',
                          maxWidth: '200px',
                          minWidth: '200px',
                        }}
                        extraCardProps={{
                          extraSmall: true,
                          showGenreLink: false,
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box px={2} pb={2}>
                    <Typography variant="h6" marginTop={3}>
                      Tracks
                    </Typography>
                    <Box my={3}>
                      <HorizontalSection
                        items={tracks}
                        cardComponent={TrackMobileCard}
                        title={null}
                        viewMoreUrl={`/labels/${id}`}
                        cardStyles={{
                          flexShrink: 0,
                          width: '200px',
                          maxWidth: '200px',
                          minWidth: '200px',
                        }}
                        extraCardProps={{
                          extraSmall: true,
                          showGenreLink: false,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Collapse>
            </TableCell>
          </TableRow>
        </>
      )}
    </>
  );
};

LabelItem.propTypes = {
  item: PropTypes.object,
  asCard: PropTypes.bool,
};

const LabelsTable = ({
  items,
  onTableInteraction,
  tableInteractionConfig,
  hideTableInteraction,
}) => {
  const [directionAsc, setDirectionAsc] = useState(false);
  const [activeCol, setActiveCol] = useState(
    hideTableInteraction ? '' : 'set_count'
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleTableHeadClick = (selectedColumn) => {
    if (hideTableInteraction) return;

    let dir = directionAsc;
    if (selectedColumn === activeCol) {
      setDirectionAsc(!directionAsc);
      dir = !dir;
    } else {
      setDirectionAsc(true);
      dir = true;
    }
    setActiveCol(selectedColumn);
    onTableInteraction({
      ...tableInteractionConfig,
      order_by: `label_meta.${selectedColumn}`,
      order_direction: dir ? 'asc' : 'desc',
    });
  };

  if (!items.length) return <></>;

  return (
    <>
      {isMobile ? (
        <Box sx={{ flexGrow: 1, textAlign: 'left' }} my={3}>
          <Grid container spacing={2}>
            {items.map((item) => (
              <Grid item xs={12} lg={3} key={item.id}>
                <LabelItem item={item} asCard />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ overflow: 'visible' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="labels table">
            <TableHead>
              <TableRow
                sx={{
                  '.MuiTableCell-root': { padding: '16px 8px' },
                }}
              >
                <TableCell
                  sx={{
                    width: '5%',
                  }}
                ></TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'name' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '30%',
                  }}
                  onClick={() => handleTableHeadClick('name')}
                >
                  Name
                  {activeCol === 'name' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'set_count' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '12.5%',
                  }}
                  onClick={() => handleTableHeadClick('set_count')}
                >
                  Sets
                  {activeCol === 'set_count' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight:
                      activeCol === 'latest_set_date' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '12.5%',
                  }}
                  onClick={() => handleTableHeadClick('latest_set_date')}
                >
                  Latest Set
                  {activeCol === 'latest_set_date' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: activeCol === 'track_count' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '12.5%',
                  }}
                  onClick={() => handleTableHeadClick('track_count')}
                >
                  Tracks
                  {activeCol === 'track_count' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight:
                      activeCol === 'latest_track_date' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    width: '12.5%',
                  }}
                  onClick={() => handleTableHeadClick('latest_track_date')}
                >
                  Latest Track
                  {activeCol === 'latest_track_date' && (
                    <>
                      {directionAsc ? (
                        <ArrowUpward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      ) : (
                        <ArrowDownward
                          fontSize="small"
                          sx={{ verticalAlign: 'top' }}
                        />
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell sx={{ width: '15%' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <LabelItem item={item} key={item.id} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

LabelsTable.propTypes = {
  items: PropTypes.array,
  onTableInteraction: PropTypes.func,
  tableInteractionConfig: PropTypes.object,
  hideTableInteraction: PropTypes.bool,
};

export default LabelsTable;
